import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { Num, Plural, T, useGT } from 'gt-react';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from 'src/components/ui/hover-card';
import { cn } from 'src/lib/utils';
import type { OpenTaskGroup, OpenTaskPreview } from '../agentPageRuns';

const PREVIEW_LIMIT = 5;

/**
 * Open-tasks tile: the count is a button that jumps to the task list, and
 * hover lists the actual work so the number is not a dead end.
 */
export function AgentPageOpenTasksStat({
  preview,
  onOpenTasksClick,
}: {
  preview: OpenTaskPreview;
  onOpenTasksClick?: () => void;
}) {
  const gt = useGT();
  const label =
    preview.count === 1
      ? gt('1 open task. Click to open the Tasks tab.', {
          context:
            'agent page open tasks stat, button that switches to the task list tab',
        })
      : gt('{count} open tasks. Click to open the Tasks tab.', {
          context:
            'agent page open tasks stat, button that switches to the task list tab',
          count: String(preview.count),
        });

  return (
    <HoverCard openDelay={150} closeDelay={100}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          aria-label={label}
          onClick={onOpenTasksClick}
          className={cn(
            'flex min-w-0 flex-1 flex-col gap-0.5 px-4 py-2.5 text-left transition-colors',
            'hover:bg-stone-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          )}
        >
          <span className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-stone-500">
            <T context="agent page stat label">Open tasks</T>
          </span>
          <span className="truncate text-base font-semibold tabular-nums text-stone-900">
            <Num>{preview.count}</Num>
          </span>
          <span className="truncate text-[11px] text-muted-foreground">
            <OpenTasksSublabel preview={preview} />
          </span>
        </button>
      </HoverCardTrigger>
      <HoverCardContent align="end" className="w-72 p-3">
        <OpenTasksHoverBody preview={preview} />
      </HoverCardContent>
    </HoverCard>
  );
}

function OpenTasksSublabel({ preview }: { preview: OpenTaskPreview }) {
  const handedOff =
    preview.handedOff.count > 0 ? (
      <>
        {' · '}
        <T context="agent page open tasks sublabel, tasks the agent escalated that now sit with a person and are not in the count">
          <Num>{preview.handedOff.count}</Num> handed off
        </T>
      </>
    ) : null;
  if (preview.count === 0) {
    return (
      <>
        <T context="agent page open tasks stat when the queue is empty">
          none waiting
        </T>
        {handedOff}
      </>
    );
  }
  return (
    <>
      <T context="agent page open tasks breakdown under the count">
        <Num>{preview.running.count}</Num> running ·{' '}
        <Num>{preview.queued.count}</Num> queued
      </T>
      {preview.needsInput.count > 0 && (
        <>
          {' · '}
          <T context="agent page open tasks breakdown, tasks assigned to the agent that wait on input">
            <Num>{preview.needsInput.count}</Num>{' '}
            <Plural
              n={preview.needsInput.count}
              one="needs input"
              other="need input"
            />
          </T>
        </>
      )}
      {handedOff}
    </>
  );
}

function OpenTasksHoverBody({ preview }: { preview: OpenTaskPreview }) {
  const handedOff =
    preview.handedOff.count > 0 ? (
      <div className="flex flex-col gap-1 border-t border-border pt-2.5">
        <TaskPreviewGroup
          title={
            <T context="open tasks hover group heading for tasks the agent escalated to a person">
              Handed off to people
            </T>
          }
          group={preview.handedOff}
        />
        <p className="text-[11px] text-muted-foreground">
          <T context="note under the handed-off group in the open tasks hover">
            Escalated by this agent and now assigned to a person. Not counted in
            open tasks.
          </T>
        </p>
      </div>
    ) : null;
  if (preview.count === 0) {
    return (
      <div className="flex flex-col gap-2.5">
        <p className="text-xs text-muted-foreground">
          <T context="agent page open tasks hover when nothing is waiting">
            No open tasks. Click to open the Tasks tab.
          </T>
        </p>
        {handedOff}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex items-center justify-between text-xs font-medium">
        <T context="heading of the open tasks hover preview">Open tasks</T>
        <span className="tabular-nums text-muted-foreground">
          <Num>{preview.count}</Num>
        </span>
      </div>
      <TaskPreviewGroup
        title={
          <T context="open tasks hover group heading for in-progress work">
            Running
          </T>
        }
        group={preview.running}
      />
      <TaskPreviewGroup
        title={
          <T context="open tasks hover group heading for queued work">Queued</T>
        }
        group={preview.queued}
      />
      <TaskPreviewGroup
        title={
          <T context="open tasks hover group heading for tasks assigned to the agent that wait on input">
            Need input
          </T>
        }
        group={preview.needsInput}
      />
      {handedOff}
      <p className="text-[11px] text-muted-foreground">
        <T context="hint under the open tasks hover list">
          Click a task to open it, or click the number to open the Tasks tab.
        </T>
      </p>
    </div>
  );
}

function TaskPreviewGroup({
  title,
  group,
}: {
  title: ReactNode;
  group: OpenTaskGroup;
}) {
  if (group.count === 0) return null;
  const shown = group.tasks.slice(0, PREVIEW_LIMIT);
  // Against the server count, so tasks the board did not load still show here.
  const remaining = group.count - shown.length;
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between text-[10.5px] font-semibold uppercase tracking-[0.06em] text-stone-500">
        <span>{title}</span>
        <span className="tabular-nums">
          <Num>{group.count}</Num>
        </span>
      </div>
      <ul className="flex flex-col gap-0.5">
        {shown.map((task) => (
          <li key={task.id}>
            <Link
              to="/tasks/$taskId"
              params={{ taskId: task.id }}
              className="block truncate text-xs text-stone-800 underline-offset-2 hover:underline"
            >
              {task.title}
            </Link>
          </li>
        ))}
      </ul>
      {remaining > 0 && (
        <p className="text-[11px] text-muted-foreground">
          <T context="more open tasks than the hover preview lists">
            +<Num>{remaining}</Num> more
          </T>
        </p>
      )}
    </div>
  );
}
